/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { bucketService } from '../../services/api';
import { BillingPeriod, BucketCostEstimate } from '../../types';

// Regex for bucket name validation: lowercase letters, numbers, hyphens only
const BUCKET_NAME_REGEX = /^[a-z0-9-]+$/;

interface FormErrors {
    name?: string;
    visibility?: string;
}

interface CreateBucketModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (data: {
        name: string;
        billing_period: BillingPeriod;
        versioning: boolean;
        is_public: boolean;
        description?: string;
    }) => Promise<void>;
    isCreating: boolean;
    apiError?: string | null;
    // Needed to fetch the live per-GB rate from /buckets/billing/estimate.
    // Optional so callers that don't have a project selected still render - we
    // just hide the rate hint in that case.
    projectId?: string;
}

const CreateBucketModal: React.FC<CreateBucketModalProps> = ({
    isOpen,
    onClose,
    onCreate,
    isCreating,
    apiError,
    projectId,
}) => {
    const [formData, setFormData] = useState({
        name: '',
        billing_period: 'month' as BillingPeriod,
        versioning: false,
        visibility: '' as 'public' | 'private' | '',
        description: '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    // Live per-GB rate from the backend. We query with size_gb=1 purely to read
    // rate_per_gb_per_hour + discount_percent - buckets have no upfront quota,
    // they're billed on actual usage, so we show a per-GB projection not a total.
    const { data: rateEstimate } = useQuery<BucketCostEstimate>({
        queryKey: ['bucket-rate', projectId, formData.billing_period],
        queryFn: () =>
            bucketService
                .estimateCost(projectId!, { size_gb: 1, billing_period: formData.billing_period })
                .then((res) => res.data as BucketCostEstimate),
        enabled: isOpen && !!projectId,
        staleTime: 60_000,
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                name: '',
                billing_period: 'month',
                versioning: false,
                visibility: '',
                description: '',
            });
            setErrors({});
            setTouched({});
        }
    }, [isOpen]);

    // Validate bucket name in real-time
    const validateName = (name: string): string | undefined => {
        if (!name.trim()) {
            return 'Bucket name is required';
        }
        if (!BUCKET_NAME_REGEX.test(name)) {
            return 'Only lowercase letters, numbers, and hyphens are allowed';
        }
        if (name.startsWith('-') || name.endsWith('-')) {
            return 'Bucket name cannot start or end with a hyphen';
        }
        if (name.length < 3) {
            return 'Bucket name must be at least 3 characters';
        }
        if (name.length > 63) {
            return 'Bucket name cannot exceed 63 characters';
        }
        return undefined;
    };

    // Validate visibility
    const validateVisibility = (visibility: string): string | undefined => {
        if (!visibility) {
            return 'Please select a visibility option';
        }
        return undefined;
    };

    // Handle name change with real-time validation
    const handleNameChange = (value: string) => {
        const lowercaseValue = value.toLowerCase();
        setFormData({ ...formData, name: lowercaseValue });

        if (touched.name) {
            const nameError = validateName(lowercaseValue);
            setErrors((prev) => ({ ...prev, name: nameError }));
        }
    };

    // Handle field blur for validation
    const handleBlur = (field: string) => {
        setTouched((prev) => ({ ...prev, [field]: true }));

        if (field === 'name') {
            const nameError = validateName(formData.name);
            setErrors((prev) => ({ ...prev, name: nameError }));
        } else if (field === 'visibility') {
            const visibilityError = validateVisibility(formData.visibility);
            setErrors((prev) => ({ ...prev, visibility: visibilityError }));
        }
    };

    // Handle visibility change
    const handleVisibilityChange = (value: 'public' | 'private') => {
        setFormData({ ...formData, visibility: value });
        setTouched((prev) => ({ ...prev, visibility: true }));
        setErrors((prev) => ({ ...prev, visibility: undefined }));
    };

    // Validate all fields before submit
    const validateForm = (): boolean => {
        const nameError = validateName(formData.name);
        const visibilityError = validateVisibility(formData.visibility);

        setErrors({
            name: nameError,
            visibility: visibilityError,
        });

        setTouched({
            name: true,
            visibility: true,
        });

        return !nameError && !visibilityError;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all fields
        if (!validateForm()) {
            return;
        }

        // Call API - modal stays open until success
        await onCreate({
            name: formData.name,
            billing_period: formData.billing_period,
            versioning: formData.versioning,
            is_public: formData.visibility === 'public',
            description: formData.description || undefined,
        });
    };

    const handleClose = () => {
        if (!isCreating) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-2xl shadow-xl w-full max-w-md mx-4 border border-gray-800">
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h3 className="text-lg font-semibold text-white">Create Bucket</h3>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-300 rounded-lg disabled:opacity-50"
                        disabled={isCreating}
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* API Error Display */}
                    {apiError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-sm text-red-400">{apiError}</p>
                        </div>
                    )}

                    {/* Bucket Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Bucket Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            onBlur={() => handleBlur('name')}
                            className={`input ${errors.name && touched.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                            placeholder="my-data-bucket"
                            disabled={isCreating}
                        />
                        {errors.name && touched.name && (
                            <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                            Lowercase letters, numbers, and hyphens only (3-63 characters)
                        </p>
                    </div>

                    {/* Visibility - Radio Buttons */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Visibility <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={formData.visibility === 'private'}
                                    onChange={() => handleVisibilityChange('private')}
                                    className="w-4 h-4 text-primary-500 border-gray-600 focus:ring-primary-500"
                                    disabled={isCreating}
                                />
                                <span className="text-sm text-gray-300">Private</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={formData.visibility === 'public'}
                                    onChange={() => handleVisibilityChange('public')}
                                    className="w-4 h-4 text-primary-500 border-gray-600 focus:ring-primary-500"
                                    disabled={isCreating}
                                />
                                <span className="text-sm text-gray-300">Public</span>
                            </label>
                        </div>
                        {errors.visibility && touched.visibility && (
                            <p className="mt-1 text-sm text-red-400">{errors.visibility}</p>
                        )}
                    </div>

                    {/* Description - Optional */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Description <span className="text-gray-400">(optional)</span>
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="input min-h-[80px] resize-none"
                            placeholder="Enter a description for this bucket..."
                            rows={3}
                            disabled={isCreating}
                        />
                    </div>

                    {/* Versioning - Toggle Switch */}
                    <div>
                        <label className="flex items-center justify-between cursor-pointer">
                            <div>
                                <span className="text-sm font-medium text-gray-300">Versioning</span>
                                <p className="text-xs text-gray-400">Keep multiple versions of objects</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, versioning: !formData.versioning })}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${formData.versioning ? 'bg-primary-500' : 'bg-gray-600'
                                    }`}
                                disabled={isCreating}
                                role="switch"
                                aria-checked={formData.versioning}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.versioning ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                />
                            </button>
                        </label>
                    </div>

                    {/* Billing Period */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Billing Period
                        </label>
                        <select
                            value={formData.billing_period}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    billing_period: e.target.value as BillingPeriod,
                                })
                            }
                            className="input"
                            disabled={isCreating}
                        >
                            <option value="month">Monthly</option>
                            <option value="quarter">Quarterly</option>
                            <option value="annual">Annual</option>
                        </select>
                    </div>

                    {/* Pricing hint - live per-GB rate from backend. Buckets are billed on
                        actual usage (hourly sampling), so we show the unit rate, not a total. */}
                    {rateEstimate && (
                        <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3">
                            <p className="text-xs font-medium text-primary-400">Pricing</p>
                            <p className="text-sm font-semibold text-primary-500 mt-0.5">
                                ₹{(rateEstimate.rate_per_gb_per_hour * 730).toFixed(2)} per GB / month
                            </p>
                            <p className="text-xs text-primary-400 mt-0.5">
                                ₹{rateEstimate.rate_per_gb_per_hour.toFixed(4)}/GB/hr
                                {rateEstimate.discount_percent > 0 &&
                                    ` (−${rateEstimate.discount_percent.toFixed(0)}% ${formData.billing_period} discount)`}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Billed on actual usage - only pay for what you store.
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn-secondary flex-1"
                            disabled={isCreating}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isCreating}
                        >
                            {isCreating ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateBucketModal;
